import { useState, useRef, useMemo, useEffect } from 'react'
import { chakra } from '@chakra-ui/react'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Card, Pressable, Text, VStack } from '@ayble-health/dashboard-components'
import { ButtonTile } from './ButtonTile'
import type { JourneyStage, LabOrderStatus, ActiveLabOrder, LabEntry, LabDeliveryMethod, OrderAttachment } from '../../models/careOSPrototype'
import { DotsThreeIcon, CheckIcon, CloseIcon, LockIcon, MessageIcon, PlusIcon, UploadIcon, CalendarIcon, FileIcon } from './icons'
import { useLabCatalog } from '../../api/careOSPrototype/useLabCatalog'
import { useLabHistory } from '../../api/careOSPrototype/useLabHistory'
import { reviewedDemoResult } from '../../mocks/fixtures/careOSPrototype'

type LabStatus = LabOrderStatus

interface LabOrderForm {
  billingType: 'insurance' | 'self-pay'
  vendor: 'quest' | 'labcorp'
  tests: string[]
  selectedPanelIds: string[]   // panels currently applied — sticky until × or Clear all
  reviewingProvider: string
  orderDate: string
}

const DEFAULT_FORM: LabOrderForm = {
  billingType: 'insurance',
  vendor: 'labcorp',
  tests: [],
  selectedPanelIds: [],
  reviewingProvider: 'Dr. Taylor Haywood',
  orderDate: 'Apr 22, 2026',
}

// ── Status helpers ─────────────────────────────────────────────────────────────

function isInFlight(s: LabStatus) {
  return s === 'submitted' || s === 'pending-collection' || s === 'processing' || s === 'pending-review'
}

function statusColor(s: LabStatus) {
  if (s === 'completed') return 'var(--scrubs)'
  if (s === 'out-of-range') return 'var(--fuschia)'
  return 'var(--orange)'
}

// ── Result pill ───────────────────────────────────────────────────────────────
// The single pill on the right of a result card, replacing the status pill +
// delivery badge pair. A manually uploaded result reads "Uploaded" — range is
// only asserted for results auto-received from e-labs direct, which read
// "In Range" / "Out of Range" once they land. Pending borrows the LabSelect
// "Not set" treatment (canvas fill, cloud hairline): both are the same
// "nothing asserted yet" state. Every pill declares a border so the four sit
// at identical heights.

function resultPill(entry: LabEntry): { label: string; bg: string; fg: string; border: string } {
  if (entry.deliveryMethod === 'uploaded') return { label: 'Uploaded',     bg: 'var(--cloud)',  fg: 'var(--evergreen)', border: 'transparent' }
  if (entry.status === 'out-of-range')     return { label: 'Out of Range', bg: 'var(--ballet)', fg: 'var(--plum)',      border: 'transparent' }
  if (entry.status === 'completed')        return { label: 'In Range',     bg: 'var(--mint)',   fg: 'var(--seafoam)',   border: 'transparent' }
  return { label: 'Pending', bg: 'var(--canvas)', fg: 'var(--evergreen)', border: 'var(--cloud)' }
}

// One renderer for both call sites — the card face and the preview drawer, which
// reports the same verdict rather than offering to change it. `md` matches the
// drawer's 12px control row; `sm` is the card's 2xs.
function ResultPill({ entry, size = 'sm' }: { entry: LabEntry; size?: 'sm' | 'md' }) {
  const pill = resultPill(entry)
  return (
    <span style={{
      fontSize: size === 'md' ? 12 : 10,
      fontWeight: 600, fontFamily: 'var(--font-body)',
      background: pill.bg, color: pill.fg,
      border: `1px solid ${pill.border}`,
      borderRadius: 100,
      padding: size === 'md' ? '5px 12px' : '2px 8px',
      whiteSpace: 'nowrap',
    }}>{pill.label}</span>
  )
}

// ── Success toast ─────────────────────────────────────────────────────────────

function SuccessToast({ title, tests, onDismiss }: { title: string; tests: string[]; onDismiss: () => void }) {
  return (
    <chakra.div
      css={{ background: '{colors.canvas}', borderRadius: '{radii.card}' }}
      style={{
        position: 'fixed', top: 64, right: 20, zIndex: 9999,
        width: 300,
        border: '1px solid var(--scrubs)',
        borderLeft: '3px solid var(--scrubs)',
        padding: '13px 14px',
        display: 'flex', alignItems: 'flex-start', gap: 10,
        boxShadow: '0 4px 20px rgba(4,51,56,0.12)',
        animation: 'toastSlideIn 220ms ease-out',
      }}
    >
      <div style={{ flexShrink: 0, marginTop: 1 }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7.25" stroke="var(--scrubs)" strokeWidth="1.5" />
          <path d="M5 8l2.2 2.2L11 5.5" stroke="var(--scrubs)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 'var(--text-cos-btn-sb-size)', fontWeight: 'var(--text-cos-btn-sb-weight)', color: 'var(--evergreen)', fontFamily: 'var(--font-body)', margin: '0 0 2px', lineHeight: 1.3 }}>
          {title}
        </p>
        <p style={{ fontSize: 12, /* xs */ color: 'var(--blue-grey)', fontFamily: 'var(--font-body)', margin: 0, lineHeight: 1.4 }}>
          {tests.join(', ')}
        </p>
      </div>
      <Pressable
        onClick={onDismiss}
        className="hover-blue-grey"
        flexShrink={0}
        color="steel"
        p={0}
        mt="1px"
        transition="color 0.15s"
        aria-label="Dismiss"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <path d="M2 2l8 8M10 2L2 10" />
        </svg>
      </Pressable>
    </chakra.div>
  )
}

// Maps the source toggle value to the vendor label that lives on the LabEntry.
// Shared between handleUploadSubmit and the synthetic-entry construction in
// LabHistoryList (when an uploaded active order transitions to terminal).
function sourceLabel(source?: 'patient-copy' | 'provider-sent'): string {
  if (source === 'provider-sent') return 'External provider'
  if (source === 'patient-copy') return 'Patient copy'
  return 'Uploaded'
}

// Source + range option sets — shared by the staged-upload rows and the
// per-attachment tagging controls in the preview drawer.
const SOURCE_OPTIONS = [
  { value: 'patient-copy', label: 'Patient Copy' },
  { value: 'provider-sent', label: 'Provider Sent' },
] as const
// Range chips carry per-option selected colors so the chosen state is
// color-coded (teal = in range, blush/plum = out of range), matching the
// status pills on the result cards.
const RANGE_OPTIONS = [
  { value: 'in-range', label: 'In Range', selectedBg: 'var(--mint)', selectedColor: 'var(--seafoam)' },
  { value: 'out-of-range', label: 'Out of Range', selectedBg: 'var(--ballet)', selectedColor: 'var(--plum)' },
] as const

// Today as an ISO date string (yyyy-mm-dd) for <input type="date"> defaults.
function todayISO(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
// Converts an ISO date (yyyy-mm-dd) to the "Mon D, YYYY" display style used on cards.
function isoToDisplay(iso: string): string {
  const [y, m, day] = iso.split('-').map(Number)
  if (!y || !m || !day) return iso
  return new Date(y, m - 1, day).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}
// Parses a "Mon D, YYYY" display date back to ISO (yyyy-mm-dd) for date inputs.
// Returns '' when the string can't be parsed, so the picker shows empty.
function displayToISO(display: string): string {
  const d = new Date(display)
  if (isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
// ISO (yyyy-mm-dd) ⇄ local Date helpers for the calendar picker.
function isoToDate(iso: string): Date | null {
  const [y, m, d] = (iso || '').split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}
function dateToISO(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// Chakra-v3-Select-style dropdown for the lab metadata pickers (Source, Result
// Range). Replaces the old OptionChips pills (PRD 6/2: standardize on Select).
// Supports an explicit "Not set" option; the Range trigger color-codes per the
// selected option's selectedBg/selectedColor (Mint+Seafoam / Ballet+Plum).
function LabSelect({ options, value, onChange, placeholder = 'Not set', width = '100%' }: {
  options: readonly { value: string; label: string; selectedBg?: string; selectedColor?: string }[]
  value?: string
  onChange: (v: string | undefined) => void
  placeholder?: string
  width?: number | string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])

  const selected = options.find(o => o.value === value)
  const triggerBg = selected?.selectedBg ?? 'white'
  const triggerColor = selected?.selectedColor ?? 'var(--evergreen)'
  const triggerBorder = selected?.selectedBg ?? 'var(--cloud)'

  const choose = (v: string | undefined) => { onChange(v); setOpen(false) }

  return (
    <div ref={ref} style={{ position: 'relative', width }}>
      {/* Not a Button: this is a combobox trigger with a listbox popover. The
          package ships `Select`, but adopting it swaps the behaviour, not just
          the chrome — a separate decision from this pass. */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', height: 28,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          padding: '0 10px', borderRadius: 6,
          border: `1px solid ${triggerBorder}`,
          background: triggerBg, color: triggerColor,
          fontSize: 12, fontWeight: selected ? 600 : 500, fontFamily: 'var(--font-body)',
          cursor: 'pointer', whiteSpace: 'nowrap', transition: 'border-color 0.15s, background 0.15s',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{selected?.label ?? placeholder}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.7 }}><path d="M2 4l4 4 4-4" /></svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, minWidth: '100%',
          background: 'white', border: '1px solid var(--cloud)', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(4,51,56,0.10)', padding: '4px 0', zIndex: 40,
        }}>
          {[{ value: undefined as string | undefined, label: placeholder }, ...options].map(opt => {
            const isSel = opt.value === value
            return (
              <Pressable
                key={opt.label}
                onClick={() => choose(opt.value)}
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                w="100%"
                textAlign="left"
                px={3}
                py="7px"
                fontSize="xs"
                fontFamily="body"
                color="evergreen"
                whiteSpace="nowrap"
                _hover={{ bg: 'var(--row-hover)' }}
              >
                {opt.label}
                {isSel && (
                  <svg width="13" height="13" viewBox="0 0 15 15" fill="none" stroke="var(--seafoam)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M12.5 4 6 10.5 2.75 7.25" /></svg>
                )}
              </Pressable>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Token-styled date selector with Month + Year dropdowns above the calendar
// grid, so a user can jump back several years in one click (e.g. uploading a
// new patient's historical labs) instead of paging month-by-month. Chakra v3
// ships no DatePicker primitive, so this is a lightweight custom build on the
// design tokens. Value is ISO (yyyy-mm-dd); empty → caller defaults to today.
const PICKER_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
function DateField({ value, onChange, maxDate = todayISO() }: {
  value: string
  onChange: (v: string) => void
  maxDate?: string
}) {
  // Auto-apply today the first time the field renders without a selection, so
  // the calendar always opens on a concrete date (today, or the prior choice).
  useEffect(() => {
    if (!value) onChange(todayISO())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The effective selection — value once set, otherwise today (for the brief
  // first render before the effect above commits it).
  const effectiveIso = value || todayISO()
  const selected = isoToDate(effectiveIso)
  const base = selected ?? new Date()
  const [viewYear, setViewYear] = useState(base.getFullYear())
  const [viewMonth, setViewMonth] = useState(base.getMonth())

  const maxObj = isoToDate(maxDate)
  const maxYear = maxObj ? maxObj.getFullYear() : new Date().getFullYear()
  const years: number[] = []
  for (let y = maxYear; y >= maxYear - 30; y--) years.push(y)

  const firstWeekday = new Date(viewYear, viewMonth, 1).getDay()
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const cells: (number | null)[] = []
  for (let i = 0; i < firstWeekday; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  const selectStyle: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--evergreen)',
    border: '1px solid var(--cloud)', borderRadius: 6,
    padding: '6px 8px', background: 'white', cursor: 'pointer', outline: 'none',
  }

  return (
    <div>
      {/* Read-only selection display */}
      <div
        style={{
          width: '100%', boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          border: '1px solid var(--cloud)', borderRadius: 6,
          padding: '8px 12px', background: 'white',
          fontSize: 14, fontFamily: 'var(--font-body)', color: 'var(--evergreen)',
        }}
      >
        <span>{isoToDisplay(effectiveIso)}</span>
        <span style={{ color: 'var(--aqua)', display: 'inline-flex' }}><CalendarIcon /></span>
      </div>

      {/* Calendar — always expanded, in normal flow so it grows the modal/form. */}
      <Card
        mt={2}
        borderWidth="1px"
        borderColor="cloud"
        borderRadius="card"
        bg="canvas"
        p={3}
      >
        {/* Month + Year dropdowns */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <select
            aria-label="Month"
            value={viewMonth}
            onChange={e => setViewMonth(Number(e.target.value))}
            style={{ ...selectStyle, flex: 1 }}
          >
            {PICKER_MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
          </select>
          <select
            aria-label="Year"
            value={viewYear}
            onChange={e => setViewYear(Number(e.target.value))}
            style={selectStyle}
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {/* Weekday header */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: 'var(--blue-grey)', fontFamily: 'var(--font-body)' }}>{d}</div>
          ))}
        </div>

        {/* Day grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {cells.map((d, i) => {
            if (d === null) return <span key={`e${i}`} />
            const dayDate = new Date(viewYear, viewMonth, d)
            const disabled = maxObj ? dayDate > maxObj : false
            const isSel = !!selected
              && selected.getFullYear() === viewYear
              && selected.getMonth() === viewMonth
              && selected.getDate() === d
            // Not a Button: a calendar day cell. The package ships `DatePicker`;
            // swapping it is a behavioural change, not a chrome change.
            return (
              <button
                key={d}
                type="button"
                disabled={disabled}
                onClick={() => onChange(dateToISO(dayDate))}
                onMouseEnter={e => { if (!disabled && !isSel) (e.currentTarget as HTMLElement).style.background = 'var(--feather)' }}
                onMouseLeave={e => { if (!isSel) (e.currentTarget as HTMLElement).style.background = 'transparent' }}
                style={{
                  height: 32, borderRadius: 100, border: 'none', padding: 0,
                  fontSize: 14, fontFamily: 'var(--font-body)',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  background: isSel ? 'var(--seafoam)' : 'transparent',
                  color: disabled ? 'var(--steel)' : isSel ? 'var(--canvas)' : 'var(--evergreen)',
                  transition: 'background 0.12s',
                }}
              >{d}</button>
            )
          })}
        </div>
      </Card>
    </div>
  )
}

// Formats an attachment timestamp for the "Partially complete" badge.
// Same calendar day → 12-hour time ("10:34 AM"); different day → short date ("Apr 22").
function formatAttachmentTime(ts: number): string {
  const d = new Date(ts)
  const now = new Date()
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  return isToday
    ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// The multi-step status stepper was removed from the user-dashboard active-order
// card — the card now uses simple "Upload" + "Mark as Read" actions instead. The
// stepper still exists on the homepage TasksPanel card (see TasksPanel.tsx). If
// the user-dashboard stepper is ever restored, the prior `LabStatusStepper`
// implementation is in the v3 build prompt history.

// ── Test composer — unified panel + tag input ─────────────────────────────────

function TestComposer({ form, onChange, testInput, setTestInput }: {
  form: LabOrderForm
  onChange: (p: Partial<LabOrderForm>) => void
  testInput: string
  setTestInput: (v: string) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const composerRef = useRef<HTMLDivElement>(null)

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)

  // Static lab catalog (tests + suggested panels) from the mock layer.
  const { tests: labTests, panels: labPanels } = useLabCatalog()

  // ── Derived: filtered catalog + fast "already selected" lookup ──────────────

  const filteredTests = useMemo(() => {
    const query = testInput.trim().toLowerCase()
    if (!query) return labTests
    return labTests.filter(t => t.toLowerCase().includes(query))
  }, [testInput, labTests])

  const selectedSet = useMemo(() => new Set(form.tests), [form.tests])

  // Reset highlight to the first ACTIVE (unselected) item when filter or selection changes.
  useEffect(() => {
    if (filteredTests.length === 0) return
    const firstActive = filteredTests.findIndex(t => !selectedSet.has(t))
    setHighlightedIndex(firstActive >= 0 ? firstActive : 0)
  }, [filteredTests, selectedSet])

  // Close dropdown on outside mousedown. Cleanup listener on unmount/close.
  useEffect(() => {
    if (!dropdownOpen) return
    const onDocMouseDown = (e: MouseEvent) => {
      if (composerRef.current && !composerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [dropdownOpen])

  // ── Handlers ────────────────────────────────────────────────────────────────

  const applyPanel = (panel: { id: string; tests: string[] }) => {
    onChange({
      tests: Array.from(new Set([...form.tests, ...panel.tests])),
      selectedPanelIds: Array.from(new Set([...form.selectedPanelIds, panel.id])),
    })
  }

  const removePanel = (panel: { id: string; tests: string[] }) => {
    const drop = new Set(panel.tests)
    onChange({
      tests: form.tests.filter(t => !drop.has(t)),
      selectedPanelIds: form.selectedPanelIds.filter(id => id !== panel.id),
    })
  }

  const removeTag = (tag: string) => {
    // Sticky: removing a single tag does NOT deselect any panel
    onChange({ tests: form.tests.filter(t => t !== tag) })
  }

  const clearAll = () => onChange({ tests: [], selectedPanelIds: [] })

  // Shared add-a-test path: dedupe, clear input, close dropdown.
  const addTest = (name: string) => {
    const trimmed = name.trim()
    if (!trimmed) return
    onChange({ tests: Array.from(new Set([...form.tests, trimmed])) })
    setTestInput('')
    setDropdownOpen(false)
  }

  // Walks the filtered list in `direction` (1 or -1) from `from`,
  // returning the index of the next active (unselected) item, or -1 if none exist.
  const nextActiveIndex = (from: number, direction: 1 | -1) => {
    if (filteredTests.length === 0) return -1
    for (let step = 1; step <= filteredTests.length; step++) {
      const idx = (from + direction * step + filteredTests.length) % filteredTests.length
      if (!selectedSet.has(filteredTests[idx])) return idx
    }
    return -1
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      setDropdownOpen(false)
      return
    }

    if (dropdownOpen && filteredTests.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const next = nextActiveIndex(highlightedIndex, 1)
        if (next >= 0) setHighlightedIndex(next)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        const next = nextActiveIndex(highlightedIndex, -1)
        if (next >= 0) setHighlightedIndex(next)
        return
      }
      if (e.key === 'Enter') {
        const candidate = filteredTests[highlightedIndex]
        if (candidate && !selectedSet.has(candidate)) {
          e.preventDefault()
          addTest(candidate)
          return
        }
        // Fall through to free-text Enter handling below
      }
    }

    // Fallbacks (preserved existing behavior)
    if (e.key === 'Enter' && testInput.trim()) {
      e.preventDefault()
      addTest(testInput.trim())
      return
    }
    if (e.key === 'Backspace' && testInput === '' && form.tests.length > 0) {
      onChange({ tests: form.tests.slice(0, -1) })
    }
  }

  const hasContent = form.tests.length > 0 || form.selectedPanelIds.length > 0

  return (
    <chakra.div>
      {/* TESTS label — Caption style (12px SemiBold, 0.04em, uppercase, Blue Grey) */}
      <chakra.p css={{
        fontSize: '12px', fontWeight: '600', color: 'var(--blue-grey)',
        letterSpacing: '0.04em', textTransform: 'uppercase',
        fontFamily: 'var(--font-body)',
        margin: '0 0 8px',
      }}>
        Tests
      </chakra.p>

      {/* Feather container */}
      <chakra.div ref={composerRef} css={{
        background: '{colors.feather}',
        borderRadius: '{radii.card}',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}>
        {/* Top row: ButtonTile chips (left, flex-1) + Clear All (right, conditional) */}
        <chakra.div css={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <chakra.div css={{
            flex: '1',
            display: 'flex', flexWrap: 'wrap', alignItems: 'center',
            gap: '8px',
          }}>
            {labPanels.map(p => {
              const selected = form.selectedPanelIds.includes(p.id)
              return (
                <ButtonTile
                  key={p.id}
                  variant="suggested"
                  selected={selected}
                  eyebrow="Suggested"
                  label={p.label}
                  endIcon={selected ? <CheckIcon size={12} /> : <PlusIcon size={10} strokeWidth={1.44} />}
                  onClick={() => selected ? removePanel(p) : applyPanel(p)}
                />
              )
            })}
          </chakra.div>

          {hasContent && (
            <chakra.button
              onClick={clearAll}
              css={{
                flexShrink: '0',
                fontSize: '14px', fontWeight: '600',
                color: '{colors.seafoam}',
                fontFamily: 'var(--font-body)',
                background: 'transparent', border: 'none',
                cursor: 'pointer',
                transition: 'color 0.15s',
                _hover: { color: '{colors.aqua}' },
              }}
            >
              Clear All
            </chakra.button>
          )}
        </chakra.div>

        {/* White input zone — borderless, click-to-focus, auto-grows with tags */}
        <chakra.div
          onClick={() => inputRef.current?.focus()}
          css={{
            background: '{colors.canvas}',
            padding: '6px',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '4px',
            cursor: 'text',
            position: 'relative',
          }}
        >
          {form.tests.map(t => (
            <Badge
              key={t}
              variant="neutral"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 2,
                padding: '4px 6px',
                fontSize: 12,
                lineHeight: 1.25,
              }}
            >
              <chakra.span>{t}</chakra.span>
              <chakra.button
                onClick={(e) => { e.stopPropagation(); removeTag(t) }}
                aria-label={`Remove ${t}`}
                css={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: '10px', height: '10px',
                  fontSize: '12px', lineHeight: '1',
                  color: 'currentColor',
                  opacity: '0.75', cursor: 'pointer',
                  background: 'transparent', border: 'none',
                  transition: 'opacity 0.15s',
                  _hover: { opacity: '1' },
                }}
              >
                ×
              </chakra.button>
            </Badge>
          ))}
          <chakra.input
            ref={inputRef}
            value={testInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTestInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setDropdownOpen(true)}
            placeholder={
              form.tests.length === 0
                ? 'Select a suggested quick panel or search for tests...'
                : 'Add another...'
            }
            css={{
              flex: '1', minWidth: '120px',
              border: 'none', outline: 'none', background: 'transparent',
              fontSize: '12px', fontWeight: '400',
              fontFamily: 'var(--font-body)',
              color: '{colors.evergreen}',
              padding: '2px 0',
              '::placeholder': { color: '{colors.bluegrey}' },
            }}
          />

          {/* Typeahead dropdown — anchored to the white input zone */}
          {dropdownOpen && (
            <chakra.div
              onClick={(e) => e.stopPropagation()}
              css={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: '0',
                right: '0',
                background: '{colors.canvas}',
                border: '1px solid {colors.cloud}',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(4,51,56,0.08)',
                maxHeight: '240px',
                overflowY: 'auto',
                padding: '4px 0',
                zIndex: '10',
              }}
            >
              {filteredTests.length === 0 ? (
                <chakra.div css={{
                  padding: '12px',
                  fontSize: '12px',
                  color: '{colors.bluegrey}',
                  fontFamily: 'var(--font-body)',
                  textAlign: 'center',
                }}>
                  No tests match "{testInput}"
                </chakra.div>
              ) : (
                filteredTests.map((test, i) => {
                  const selected = selectedSet.has(test)
                  const highlighted = i === highlightedIndex && !selected
                  return (
                    <chakra.div
                      key={test}
                      onMouseEnter={() => { if (!selected) setHighlightedIndex(i) }}
                      onClick={() => { if (!selected) addTest(test) }}
                      css={{
                        padding: '8px 12px',
                        fontSize: '14px',
                        fontFamily: 'var(--font-body)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                        color: selected
                          ? '{colors.steel}'
                          : highlighted
                            ? '{colors.seafoam}'
                            : '{colors.evergreen}',
                        background: highlighted ? '{colors.feather}' : 'transparent',
                        cursor: selected ? 'not-allowed' : 'pointer',
                        transition: 'background 0.1s, color 0.1s',
                      }}
                    >
                      <chakra.span>{test}</chakra.span>
                      {selected && (
                        <chakra.span css={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '{colors.bluegrey}',
                          fontFamily: 'var(--font-body)',
                          flexShrink: '0',
                        }}>
                          Added
                        </chakra.span>
                      )}
                    </chakra.div>
                  )
                })
              )}
            </chakra.div>
          )}
        </chakra.div>
      </chakra.div>
    </chakra.div>
  )
}

// ── Lab history list ──────────────────────────────────────────────────────────

function LabSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      fontSize: 10, fontWeight: 600, /* 2xs */ color: 'var(--blue-grey)',
      letterSpacing: '0.08em', textTransform: 'uppercase',
      fontFamily: 'var(--font-body)',
      margin: '0 0 8px',
    }}>{children}</p>
  )
}

// v4: the clinician who placed the order is implicitly the reviewer for the
// whole order (single-reviewer model). Surfaced read-only on the card + drawer.
const ORDERING_CLINICIAN = 'Taylor Haywood, MD'

function LabOrderListCard({
  entry, highlightId,
  onUploadResults,
  onMarkAsRead,
  onPreview,
  attachmentCount = 0,
  latestAttachment,
  uploadOpen,
  onUploadSubmit,
  onUploadCancel,
}: {
  entry: LabEntry
  highlightId?: string
  onUploadResults?: () => void
  onMarkAsRead?: () => void
  onPreview?: () => void
  attachmentCount?: number
  latestAttachment?: OrderAttachment
  uploadOpen?: boolean
  onUploadSubmit?: (form: LabUploadForm) => void
  onUploadCancel?: () => void
}) {
  const isActiveOrder = entry.id === 'active-order'
  const isExpanded = uploadOpen === true && isActiveOrder
  const isClickable = isActiveOrder && !!onPreview && !isExpanded

  // Small outline action button — shared between Upload and Mark as Read.

  return (
    <chakra.div
      onClick={isClickable ? () => onPreview!() : undefined}
      css={{ background: '{colors.canvas}', borderRadius: '{radii.card}' }}
      style={{
        border: '1px solid var(--cloud)',
        ...(entry.status !== 'completed' ? { borderLeft: `3px solid ${statusColor(entry.status)}` } : {}),
        padding: '12px 14px',
        transition: 'background 0.6s, box-shadow 0.15s',
        cursor: isClickable ? 'pointer' : 'default',
        ...(highlightId === entry.id ? { background: 'var(--feather)' } : {}),
      }}
      onMouseEnter={isClickable ? e => ((e.currentTarget as HTMLElement).style.boxShadow = '0 2px 10px rgba(4,51,56,0.08)') : undefined}
      onMouseLeave={isClickable ? e => ((e.currentTarget as HTMLElement).style.boxShadow = 'none') : undefined}
    >
      {/* Meta row — title + chip/date on the left, actions + reviewer on the right.
          alignItems: flex-start so the left + right columns align at the top. */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 'var(--text-cos-btn-sb-size)', fontWeight: 'var(--text-cos-btn-sb-weight)', color: 'var(--evergreen)', fontFamily: 'var(--font-body)', margin: '0 0 4px' }}>
            {entry.tests.join(', ')}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-body)',
              background: 'var(--feather)', color: 'var(--seafoam)',
              borderRadius: 100, padding: '2px 8px',
            }}>{entry.vendor}</span>
            {attachmentCount > 0 && (
              <span style={{
                fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-body)',
                background: 'var(--status-pending-bg)', color: 'var(--status-pending-fg)',
                borderRadius: 100, padding: '2px 8px',
                whiteSpace: 'nowrap',
              }}>
                Partial Complete
                {latestAttachment?.uploadedTimestamp
                  ? ` · ${formatAttachmentTime(latestAttachment.uploadedTimestamp)}`
                  : latestAttachment?.uploadedAt
                    ? ` · ${latestAttachment.uploadedAt}`
                    : ''}
              </span>
            )}
            <span style={{ fontSize: 'var(--text-xs-size)', color: 'var(--blue-grey)', fontFamily: 'var(--font-body)' }}>{entry.date}</span>
          </div>
        </div>
        {!isExpanded && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {onUploadResults && (
                <Button
                  variant="outline"
                  size="xs"
                  onClick={(e) => { e.stopPropagation(); onUploadResults() }}
                >
                  ↑ Upload
                </Button>
              )}
              {onMarkAsRead && (
                <Button
                  variant="outline"
                  size="xs"
                  onClick={(e) => { e.stopPropagation(); if (attachmentCount > 0) onMarkAsRead() }}
                  disabled={attachmentCount === 0}
                  title={attachmentCount === 0 ? 'Attach at least one result to mark this order as reviewed.' : undefined}
                >
                  Mark as Reviewed
                </Button>
              )}
            </div>
            {/* v4: single reviewer (the ordering clinician), read-only. */}
            {isActiveOrder && (
              <span style={{ fontSize: 12, color: 'var(--blue-grey)', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap' }}>
                Reviewer: <span style={{ color: 'var(--seafoam)', fontWeight: 600 }}>{ORDERING_CLINICIAN}</span>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Attached files row — count + first filename. Only when files exist and card isn't expanded. */}
      {attachmentCount > 0 && !isExpanded && (
        <div style={{
          marginTop: 10,
          paddingTop: 10,
          borderTop: '1px solid var(--cloud)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          fontFamily: 'var(--font-body)',
        }}>
          <span style={{ color: 'var(--aqua)', display: 'inline-flex' }}>
            <UploadIcon />
          </span>
          <span style={{ fontWeight: 600, color: 'var(--seafoam)' }}>
            {latestAttachment?.fileName ?? entry.fileName ?? `${attachmentCount} attachment${attachmentCount !== 1 ? 's' : ''}`}
          </span>
          {attachmentCount > 1 && (
            <>
              <span style={{ color: 'var(--steel)' }}>·</span>
              <span style={{ color: 'var(--blue-grey)' }}>{attachmentCount} total</span>
            </>
          )}
          <span style={{ color: 'var(--steel)' }}>·</span>
          <span style={{ color: 'var(--blue-grey)' }}>Click to preview</span>
        </div>
      )}

      {/* Expanded upload section — only on the active-order card when uploadOpen */}
      {isExpanded && onUploadSubmit && onUploadCancel && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 16,
            borderTop: '1px solid var(--cloud)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <LabUploadFormBody onSubmit={onUploadSubmit} onCancel={onUploadCancel} />
        </div>
      )}
    </chakra.div>
  )
}

type LabResultModalType = 'edit-name' | 'edit-date' | 'delete'

// Shared kebab + dropdown of result actions (Edit name / Update date /
// Download PDF / Delete). Reused by the result tiles AND the preview-drawer
// toolbar. Stops click propagation so it never triggers a parent card/tile
// select. `entry` may be a real history LabEntry or a synthesized
// `id: 'active-order'` entry (the update/delete handlers special-case that id).
function ResultActionsMenu({ entry, onOpenModal, onAddResult, iconColor = 'var(--steel)' }: {
  entry: LabEntry
  onOpenModal?: (entry: LabEntry, type: LabResultModalType) => void
  // "Add result" isn't a modal — the tile opens the preview drawer in upload
  // mode; the drawer's own menu flips itself to upload mode. Hidden if unset.
  onAddResult?: (entry: LabEntry) => void
  iconColor?: string
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  const choose = (type: LabResultModalType | 'add-result' | null) => {
    setMenuOpen(false)
    if (type === 'add-result') onAddResult?.(entry)
    else if (type) onOpenModal?.(entry, type)
  }

  return (
    <div ref={menuRef} style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
      <Pressable
        className="hover-seafoam"
        aria-label="Result actions"
        onClick={() => setMenuOpen(o => !o)}
        style={{ color: iconColor }}
        p="2px"
        borderRadius="sm"
        flexDirection="row"
        alignItems="center"
        transition="color 0.15s"
      >
        <DotsThreeIcon />
      </Pressable>
      {menuOpen && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0,
          background: 'white', border: '1px solid var(--cloud)', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(4,51,56,0.10)', padding: '4px 0',
          minWidth: 168, zIndex: 30,
        }}>
          {([
            ...(onAddResult ? [{ label: 'Add result', type: 'add-result' as const, danger: false }] : []),
            { label: 'Edit result name',   type: 'edit-name' as const, danger: false },
            { label: 'Update results date', type: 'edit-date' as const, danger: false },
            { label: 'Download PDF',        type: null,                 danger: false },
            { label: 'Delete result',       type: 'delete' as const,    danger: true  },
          ]).map(({ label, type, danger }) => (
            <Pressable
              key={label}
              onClick={() => choose(type)}
              w="100%"
              textAlign="left"
              px={3}
              py="7px"
              fontSize="xs"
              fontFamily="body"
              color={danger ? 'fuschia' : 'evergreen'}
              transition="background 0.1s"
              _hover={{
                bg: danger ? 'var(--row-hover-danger)' : 'var(--row-hover)',
              }}
            >
              {label}
            </Pressable>
          ))}
        </div>
      )}
    </div>
  )
}

function LabResultCard({
  entry,
  onOpenModal,
  onAddResult,
  onSelect,
}: {
  entry: LabEntry
  onOpenModal?: (entry: LabEntry, type: LabResultModalType) => void
  onAddResult?: (entry: LabEntry) => void
  onSelect?: (entry: LabEntry) => void
}) {
  const [hovered, setHovered] = useState(false)
  const isOutOfRange = entry.status === 'out-of-range'
  const fileCount = entry.attachments?.length ?? 1

  return (
    <chakra.div
      css={{ background: '{colors.canvas}', borderRadius: '{radii.card}' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect?.(entry)}
      role={onSelect ? 'button' : undefined}
      style={{
        border: '1px solid var(--cloud)',
        borderTop: isOutOfRange ? '3px solid var(--plum)' : '1px solid var(--cloud)',
        padding: '12px 12px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minHeight: 130,
        position: 'relative',
        transition: 'box-shadow 0.15s',
        boxShadow: hovered ? '0 2px 10px rgba(4,51,56,0.08)' : 'none',
        cursor: onSelect ? 'pointer' : 'default',
      }}
    >
      {/* Dot menu — top-right (stops propagation so it won't trigger card select) */}
      <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 5 }}>
        <ResultActionsMenu entry={entry} onOpenModal={onOpenModal} onAddResult={onAddResult} />
      </div>

      {/* Test names — 2-line clamp */}
      <p style={{
        fontSize: 'var(--text-cos-btn-sb-size)', fontWeight: 'var(--text-cos-btn-sb-weight)', color: 'var(--evergreen)',
        fontFamily: 'var(--font-body)',
        margin: 0,
        lineHeight: 1.35,
        paddingRight: 20,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      } as React.CSSProperties}>
        {entry.tests.join(', ')}
      </p>

      {/* Vendor + date */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 10, fontWeight: 600, fontFamily: 'var(--font-body)',
          background: 'var(--feather)', color: 'var(--seafoam)',
          borderRadius: 100, padding: '2px 7px',
        }}>{entry.vendor}</span>
        <span style={{ fontSize: 12, color: 'var(--blue-grey)', fontFamily: 'var(--font-body)' }}>{entry.date}</span>
        {/* File count — only once a result has been added, so a single-file
            tile reads exactly as before. */}
        {fileCount > 1 && (
          <Text as="span" fontSize="xs" color="bluegrey" my={0}>
            · {fileCount} files
          </Text>
        )}
      </div>

      {/* Footer: status pill — pinned to bottom-right */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        marginTop: 'auto',
      }}>
        <ResultPill entry={entry} />
      </div>
    </chakra.div>
  )
}

// ── Full-page modal for lab result actions ────────────────────────────────────

interface LabResultModalState {
  type: LabResultModalType
  entry: LabEntry
}

function LabResultModal({
  state,
  onClose,
  onUpdate,
  onDelete,
}: {
  state: LabResultModalState
  onClose: () => void
  onUpdate: (id: string, updates: Partial<LabEntry>) => void
  onDelete: (id: string) => void
}) {
  const [editValue, setEditValue] = useState(
    state.type === 'edit-name'
      ? state.entry.tests.join(', ')
      : displayToISO(state.entry.date) // edit-date holds an ISO value for the date picker
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const handleSave = () => {
    if (state.type === 'edit-name') {
      const tests = editValue.split(',').map(t => t.trim()).filter(Boolean)
      onUpdate(state.entry.id, { tests: tests.length ? tests : state.entry.tests })
    } else if (state.type === 'edit-date') {
      // editValue is ISO from the date picker → store back in display format.
      onUpdate(state.entry.id, { date: editValue ? isoToDisplay(editValue) : state.entry.date })
    }
    onClose()
  }

  const handleDelete = () => {
    onDelete(state.entry.id)
    onClose()
  }

  const isDelete = state.type === 'delete'
  const title = state.type === 'edit-name' ? 'Edit result name'
    : state.type === 'edit-date' ? 'Update results date'
    : 'Delete result'

  const inputCss: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    border: '1px solid var(--cloud)', borderRadius: 6,
    padding: '8px 12px',
    fontSize: 14, fontFamily: 'var(--font-body)', color: 'var(--evergreen)',
    outline: 'none',
    transition: 'border-color 0.15s',
  }

  return (
    <>
      {/* Backdrop — same rgba as the preview drawer */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(4,51,56,0.45)',
          zIndex: 300,
        }}
      />

      {/* Centering wrapper — sits above backdrop, does not block pointer on empty areas */}
      <div
        style={{
          position: 'fixed', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 301,
          padding: 24,
        }}
        onClick={onClose}
      >
        {/* Dialog card */}
        <chakra.div
          css={{
            background: '{colors.canvas}',
            borderRadius: '{radii.card}',
            boxShadow: '{shadows.card}',
          }}
          style={{
            width: state.type === 'edit-name' ? 480 : 400,
            maxWidth: '100%',
            animation: 'modalFadeIn 160ms ease-out',
            overflow: 'hidden',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '20px 20px 0',
          }}>
            <chakra.p
              css={{ fontFamily: '{fonts.body}', color: '{colors.evergreen}' }}
              style={{ margin: 0, fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}
            >
              {title}
            </chakra.p>
            <Pressable
              onClick={onClose}
              aria-label="Close"
              color="bluegrey"
              flexDirection="row"
              alignItems="center"
              p={1}
              borderRadius="sm"
              transition="color 0.15s"
            >
              <CloseIcon />
            </Pressable>
          </div>

          {/* Body */}
          <div style={{ padding: '16px 20px' }}>
            {state.type === 'edit-name' && (
              <input
                autoFocus
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSave() }}
                style={inputCss}
              />
            )}
            {state.type === 'edit-date' && (
              <DateField value={editValue} onChange={setEditValue} />
            )}
            {isDelete && (
              <chakra.p
                css={{ fontFamily: '{fonts.body}', color: '{colors.bluegrey}' }}
                style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}
              >
                <chakra.span css={{ color: '{colors.evergreen}', fontWeight: '600' }}>
                  "{state.entry.tests.join(', ')}"
                </chakra.span>{' '}
                will be permanently removed. This action cannot be undone.
              </chakra.p>
            )}
          </div>

          {/* Footer */}
          <chakra.div
            css={{ borderTop: '1px solid {colors.cloud}' }}
            style={{
              display: 'flex', justifyContent: 'flex-end', gap: 8,
              padding: '14px 20px',
            }}
          >
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            {isDelete ? (
              <chakra.button
                css={{
                  fontFamily: '{fonts.body}',
                  borderRadius: '{radii.pill}',
                  background: '{colors.fuschia}',
                  color: '{colors.canvas}',
                  _hover: { background: '{colors.plum}' },
                }}
                style={{
                  // Match ui/Button md (32px / px 16) so Cancel + Delete are
                  // a consistent size side by side.
                  height: 32, padding: '0 16px',
                  fontSize: 14, fontWeight: 600,
                  border: 'none', cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onClick={handleDelete}
              >
                Delete
              </chakra.button>
            ) : (
              <Button variant="solid" onClick={handleSave}>Save</Button>
            )}
          </chakra.div>
        </chakra.div>
      </div>
    </>
  )
}

// The active order as a LabEntry, so it can sit in the same list as history
// results (always id 'active-order' — the update/delete/add handlers
// special-case that id). Shared by LabHistoryList and the drawer's live lookup.
function activeOrderAsEntry(activeLabOrder: ActiveLabOrder): LabEntry {
  const attachments = activeLabOrder.attachments ?? []
  const firstAttachment = attachments[0]
  const lastAttachment = attachments[attachments.length - 1]
  const hasAttachments = attachments.length > 0
  const isTerminal = activeLabOrder.status === 'completed' || activeLabOrder.status === 'out-of-range'
  // Terminal: derive deliveryMethod from whether anything was uploaded.
  // - uploaded files present → 'uploaded'
  // - no uploads, marked-as-read came in via the integration → 'auto-received'
  // In-flight: 'in-flight'.
  const deliveryMethod: LabDeliveryMethod = isTerminal
    ? (hasAttachments ? 'uploaded' : 'auto-received')
    : 'in-flight'
  return {
    id: 'active-order',
    date: activeLabOrder.orderedDate,
    status: activeLabOrder.status as LabStatus,
    tests: activeLabOrder.tests,
    // Terminal + uploaded → vendor reflects the upload source
    // (patient went out-of-network). Otherwise keep the order's lab vendor.
    vendor: hasAttachments && isTerminal
      ? sourceLabel(firstAttachment.source)
      : activeLabOrder.vendor,
    deliveryMethod,
    fileName: lastAttachment?.fileName,
    // Full file list, so a terminal order's tile + drawer show every
    // attachment (and "Add result" appends to the order itself).
    attachments: hasAttachments ? attachments : undefined,
  }
}

// A history result's files. Entries that have had a result added carry the
// full list in `attachments`; older ones only know their single `fileName`,
// so synthesize that one file (Source/Range derived from the entry).
function primaryAttachment(entry: LabEntry): OrderAttachment {
  return {
    id: entry.id,
    fileName: entry.fileName ?? `${entry.tests.join(', ')}.pdf`,
    source: entry.deliveryMethod === 'uploaded' ? 'patient-copy' : 'provider-sent',
    range: entry.status === 'out-of-range' ? 'out-of-range' : entry.status === 'completed' ? 'in-range' : undefined,
    uploadedAt: entry.date,
  }
}
function resultAttachments(entry: LabEntry): OrderAttachment[] {
  return entry.attachments?.length ? entry.attachments : [primaryAttachment(entry)]
}
// A result's status follows its files: any out-of-range file flips it.
function statusFromAttachments(attachments: OrderAttachment[]): LabStatus {
  return attachments.some(a => a.range === 'out-of-range') ? 'out-of-range' : 'completed'
}

function LabHistoryList({
  entries, highlightId, activeLabOrder,
  onUploadActiveOrder,
  onMarkAsRead,
  onPreviewActiveOrder,
  uploadOpen,
  onUploadSubmit,
  onUploadCancel,
  onUpdateEntry,
  onDeleteEntry,
  onOpenModal,
  onAddResult,
  onSelectResult,
}: {
  entries: LabEntry[]
  highlightId?: string
  activeLabOrder: ActiveLabOrder | null
  onUploadActiveOrder?: () => void
  onMarkAsRead?: () => void
  onPreviewActiveOrder?: () => void
  uploadOpen?: boolean
  onUploadSubmit?: (form: LabUploadForm) => void
  onUploadCancel?: () => void
  onUpdateEntry?: (id: string, updates: Partial<LabEntry>) => void
  onDeleteEntry?: (id: string) => void
  onOpenModal?: (entry: LabEntry, type: LabResultModalType) => void
  onAddResult?: (entry: LabEntry) => void
  onSelectResult?: (entry: LabEntry) => void
}) {
  const allEntries: LabEntry[] = activeLabOrder
    ? [activeOrderAsEntry(activeLabOrder), ...entries]
    : entries

  const inFlightEntries  = allEntries.filter(e => isInFlight(e.status))
  const terminalEntries  = allEntries.filter(e => !isInFlight(e.status))
  const hasBoth = inFlightEntries.length > 0 && terminalEntries.length > 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>

      {/* ── Active orders: full-width list cards with stepper ── */}
      {inFlightEntries.length > 0 && (
        <div style={{ marginBottom: hasBoth ? 20 : 0 }}>
          {hasBoth && <LabSectionLabel>Active Orders</LabSectionLabel>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {inFlightEntries.map(entry => (
              <LabOrderListCard
                key={entry.id}
                entry={entry}
                highlightId={highlightId}
                // Only the injected active-order card gets the action affordances —
                // it's the only card that resolves a real ActiveLabOrder.
                onUploadResults={entry.id === 'active-order' ? onUploadActiveOrder : undefined}
                onMarkAsRead={entry.id === 'active-order' ? onMarkAsRead : undefined}
                onPreview={entry.id === 'active-order' ? onPreviewActiveOrder : undefined}
                attachmentCount={entry.id === 'active-order' ? (activeLabOrder?.attachments?.length ?? 0) : 0}
                latestAttachment={(() => {
                  if (entry.id !== 'active-order') return undefined
                  const atts = activeLabOrder?.attachments
                  return atts && atts.length > 0 ? atts[atts.length - 1] : undefined
                })()}
                uploadOpen={uploadOpen}
                onUploadSubmit={onUploadSubmit}
                onUploadCancel={onUploadCancel}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Terminal results: document card grid ── */}
      {terminalEntries.length > 0 && (
        <div>
          {hasBoth && <LabSectionLabel>Results</LabSectionLabel>}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 10,
          }}>
            {terminalEntries.map(entry => (
              <LabResultCard
                key={entry.id}
                entry={entry}
                onOpenModal={onOpenModal}
                onAddResult={onAddResult}
                onSelect={onSelectResult}
              />
            ))}
          </div>
        </div>
      )}

    </div>
  )
}

// ── Upload modal ─────────────────────────────────────────────────────────────

interface StagedUpload {
  id: string
  file: File
  fileName: string
  // Optional — left undefined until the provider designates them. No default.
  source?: 'patient-copy' | 'provider-sent'
  resultRange?: 'in-range' | 'out-of-range'
}

interface LabUploadForm {
  uploads: StagedUpload[]
  // ISO yyyy-mm-dd of the date results were completed. Only collected for
  // standalone uploads (outside an active order). Empty → defaults to today.
  completedDate?: string
  // Existing result/order card to attach the files to. Unset → the upload
  // creates new result cards (one per file), as before.
  targetEntryId?: string
}

const DEFAULT_UPLOAD_FORM: LabUploadForm = {
  uploads: [],
  completedDate: '',
}

// Inner upload form — owns its state and renders drop zone + toggles + footer.
// Used by both the standalone LabUploadFormInline AND inline inside an expanded
// LabOrderListCard (active-order resolution).
// Caption label — used inside the upload form
const uploadCaptionStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: 'var(--blue-grey)',
  letterSpacing: '0.04em', textTransform: 'uppercase',
  fontFamily: 'var(--font-body)',
  margin: '0 0 8px',
}

// One staged-upload row: file metadata + per-file source/range chips + remove
function StagedFileRow({ upload, onChange, onRemove }: {
  upload: StagedUpload
  onChange: (patch: Partial<StagedUpload>) => void
  onRemove: () => void
}) {
  return (
    <Card
      borderWidth="1px"
      borderColor="cloud"
      borderRadius="card"
      bg="canvas"
      p="14px"
      display="flex"
      flexDirection="column"
      gap={3}
    >
      {/* File metadata row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="var(--aqua)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <path d="M11 2v6h6M7 12h6M7 15h4" />
        </svg>
        {/* Editable filename — providers rename the raw upload inline (e.g.
            "IMG_2942.pdf" → "Calprotectin Feb 2026"). Cloud border at rest,
            Aqua on focus. Size text removed so the × aligns to the input center. */}
        <input
          value={upload.fileName}
          onChange={e => onChange({ fileName: e.target.value })}
          aria-label="Result file name"
          style={{
            flex: 1, minWidth: 0,
            fontSize: 14, fontWeight: 600, color: 'var(--evergreen)', fontFamily: 'var(--font-body)',
            border: '1px solid var(--cloud)', borderRadius: 6,
            padding: '6px 10px', background: 'white', outline: 'none',
            transition: 'border-color 0.15s',
          }}
          onFocus={e => (e.currentTarget.style.borderColor = 'var(--aqua)')}
          onBlur={e => (e.currentTarget.style.borderColor = 'var(--cloud)')}
        />
        <Pressable
          onClick={onRemove}
          aria-label={`Remove ${upload.fileName}`}
          fontSize="15px"
          color="bluegrey"
          flexShrink={0}
          lineHeight={1}
        >
          ×
        </Pressable>
      </div>

      {/* Per-file Source + Result Range — both optional. Chips start unselected;
          providers can leave them blank now and designate later from the drawer. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <p style={{ ...uploadCaptionStyle, margin: '0 0 6px' }}>Source <span style={{ textTransform: 'none', letterSpacing: 0, color: 'var(--steel)', fontWeight: 500 }}>(optional)</span></p>
          <LabSelect
            options={SOURCE_OPTIONS}
            value={upload.source}
            onChange={v => onChange({ source: v as StagedUpload['source'] })}
          />
        </div>
        <div>
          <p style={{ ...uploadCaptionStyle, margin: '0 0 6px' }}>Result Range <span style={{ textTransform: 'none', letterSpacing: 0, color: 'var(--steel)', fontWeight: 500 }}>(optional)</span></p>
          <LabSelect
            options={RANGE_OPTIONS}
            value={upload.resultRange}
            onChange={v => onChange({ resultRange: v as StagedUpload['resultRange'] })}
          />
        </div>
      </div>
    </Card>
  )
}

// Wraps raw File objects as staged uploads. Source + Result Range are left
// UNDEFINED — they're optional and the provider designates them per-row (or
// later, from the preview drawer). No forced default.
function stageFiles(files: File[]): StagedUpload[] {
  const stamp = Date.now()
  return files.map((file, i) => ({
    id: `staged-${stamp}-${i}-${file.name}`,
    file,
    fileName: file.name,
  }))
}

function LabUploadFormBody({ onSubmit, onCancel, progressive = false, showDateField = false, initialFiles, existingResults }: {
  onSubmit: (form: LabUploadForm) => void
  onCancel: () => void
  // Files dropped onto the preview drawer before the form opened — staged on
  // mount so the drop lands straight in the tagging rows.
  initialFiles?: File[]
  // When set, shows an "Add to" picker so a standalone upload can attach to
  // an existing card instead of creating a new one.
  existingResults?: LabEntry[]
  // When true, the form starts as a drop zone only — file rows + footer reveal
  // themselves after the first file is attached. Used by the preview drawer's
  // empty state. In non-progressive mode the footer is always visible (with
  // Upload Results disabled until something is staged).
  progressive?: boolean
  // When true, shows a "Date Results Completed" picker. Only used for standalone
  // uploads outside an active order — active-order uploads inherit the order date.
  showDateField?: boolean
}) {
  const [form, setForm] = useState<LabUploadForm>(() => ({
    ...DEFAULT_UPLOAD_FORM,
    uploads: stageFiles(initialFiles ?? []),
  }))

  const addFiles = (files: File[]) => {
    if (files.length === 0) return
    setForm(f => ({ ...f, uploads: [...f.uploads, ...stageFiles(files)] }))
  }

  const updateUpload = (id: string, patch: Partial<StagedUpload>) => {
    setForm(f => ({ ...f, uploads: f.uploads.map(u => u.id === id ? { ...u, ...patch } : u) }))
  }

  const removeUpload = (id: string) => {
    setForm(f => ({ ...f, uploads: f.uploads.filter(u => u.id !== id) }))
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    addFiles(files)
    // Clear the input so re-selecting the same file fires onChange again
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const files = Array.from(e.dataTransfer.files)
    addFiles(files)
  }

  const hasUploads = form.uploads.length > 0
  const target = existingResults?.find(e => e.id === form.targetEntryId)
  // Footer visibility: always shown in non-progressive mode (with Upload
  // Results disabled until at least one file is staged); in progressive mode
  // (drawer empty state) it stays hidden until the first file lands.
  const showFooter = !progressive || hasUploads
  // Drop zone height: large invitation in progressive empty state, compact
  // "drop more" once files are staged or in non-progressive mode.
  const dropZoneMinHeight = progressive && !hasUploads ? 320 : (hasUploads ? 80 : 100)

  return (
    <chakra.div css={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Add to — attach to an existing card, or leave as a new result. */}
      {existingResults && existingResults.length > 0 && (
        <VStack alignItems="stretch" gap={0}>
          <Text style={uploadCaptionStyle}>Add To</Text>
          <LabSelect
            options={existingResults.map(e => ({
              value: e.id,
              label: `${e.tests.join(', ')} · ${isInFlight(e.status) ? 'Ordered ' : ''}${e.date}`,
            }))}
            value={form.targetEntryId}
            onChange={v => setForm(f => ({ ...f, targetEntryId: v }))}
            placeholder="New result"
          />
          <Text fontSize="xs" color="bluegrey" mt="6px" mb={0}>
            {target
              ? `Files will be added to ${target.tests.join(', ')} (${target.vendor}, ${target.date}).`
              : 'Each file becomes its own result card — or pick an existing lab to add these files to it.'}
          </Text>
        </VStack>
      )}

      {/* Completion date — standalone uploads only, and only for a new result
          (an existing card keeps its own date). Defaults to today if blank. */}
      {showDateField && !target && (
        <div>
          <p style={uploadCaptionStyle}>Date Results Completed</p>
          <DateField
            value={form.completedDate ?? ''}
            onChange={v => setForm(f => ({ ...f, completedDate: v }))}
          />
          <p style={{ fontSize: 12, color: 'var(--blue-grey)', margin: '6px 0 0', fontFamily: 'var(--font-body)' }}>
            Defaults to today if left empty — otherwise pick the date the labs were completed.
          </p>
        </div>
      )}

      {/* Staged file rows — one per upload */}
      {hasUploads && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {form.uploads.map(upload => (
            <StagedFileRow
              key={upload.id}
              upload={upload}
              onChange={patch => updateUpload(upload.id, patch)}
              onRemove={() => removeUpload(upload.id)}
            />
          ))}
        </div>
      )}

      {/* Drop zone — always visible. Label adapts based on whether files are staged. */}
      <div>
        {!progressive && !hasUploads && <p style={uploadCaptionStyle}>Lab Documents</p>}
        <div
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          style={{
            border: '1.5px dashed var(--cloud)', borderRadius: 8, background: 'var(--bg-subtle)',
            minHeight: dropZoneMinHeight,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 6, padding: '16px 20px',
          }}
        >
          <span style={{ color: 'var(--steel)', display: 'inline-flex' }}>
            <UploadIcon />
          </span>
          <p style={{ fontSize: 'var(--text-xs-size)', color: 'var(--blue-grey)', margin: 0, fontFamily: 'var(--font-body)' }}>
            {hasUploads ? 'Drop more files here' : 'Drag and drop Results (PDFs or JPGs)'}
          </p>
          <label style={{ fontSize: 'var(--text-xs-size)', color: 'var(--aqua)', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
            {hasUploads ? 'or browse files' : 'or choose files from your device to upload'}
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
          </label>
        </div>
      </div>

      {/* Footer — Cancel + Upload Results. Disabled until at least one file is staged. */}
      {showFooter && (
        <div style={{
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
          gap: 24,
        }}>
          <Button variant="link" onClick={onCancel}>Cancel</Button>
          <Button variant="solid" onClick={() => onSubmit(form)} disabled={!hasUploads}>
            {form.uploads.length > 1 ? `Upload ${form.uploads.length} Results` : 'Upload Results'}
          </Button>
        </div>
      )}
    </chakra.div>
  )
}

// Thin wrapper: card chrome + title + LabUploadFormBody.
// Used for the generic "↑ Upload Results" header trigger. Active-order
// resolution renders the body inline inside LabOrderListCard instead.
function LabUploadFormInline({ onSubmit, onClose, title, showDateField = false, existingResults }: {
  onSubmit: (form: LabUploadForm) => void
  onClose: () => void
  title?: string
  showDateField?: boolean
  existingResults?: LabEntry[]
}) {
  return (
    <chakra.div
      css={{
        background: '{colors.canvas}',
        border: '1px solid {colors.cloud}',
        borderRadius: '{radii.card}',
        padding: '24px',
        marginBottom: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      }}
    >
      <h2 style={{
        fontSize: 15, fontWeight: 600, color: 'var(--evergreen)',
        fontFamily: 'var(--font-body)',
        margin: 0, lineHeight: 1.25,
      }}>
        {title ?? 'Upload Lab Results'}
      </h2>

      <LabUploadFormBody onSubmit={onSubmit} onCancel={onClose} showDateField={showDateField} existingResults={existingResults} />
    </chakra.div>
  )
}

// ── Active order preview modal ────────────────────────────────────────────────

// Right-side slide-in drawer for previewing the actual PDFs attached to the
// active order. The PDF is rendered via a native browser <iframe>, which picks
// up the browser's built-in PDF viewer. Navigation lives entirely in the
// bottom row so the PDF can fill the whole body area.
function LabOrderPreviewDrawer({
  activeLabOrder, result, onClose, onInlineUpload,
  onQuickMessage, onDiscussWithPatient, onNotifyTeam, onMarkAsRead,
  onUpdateAttachment, onUpdateEntry, onOpenResultMenu,
  onAddToResult, onUpdateResultAttachment, initialMode,
}: {
  activeLabOrder: ActiveLabOrder | null
  // When set, the drawer displays this past history result (clicked from a
  // result tile) instead of the active order. Source/Range derive from the
  // entry; Upload / Mark as Read are hidden (the result is already terminal).
  result?: LabEntry | null
  onClose: () => void
  // Called when the user submits an upload from inside the drawer. The drawer
  // stays open — once attachments land, the body transitions back to the PDF
  // viewer (or to the new attachment if none existed before).
  onInlineUpload: (form: LabUploadForm) => void
  // Patient-communication actions, surfaced in the action toolbar in view mode.
  onQuickMessage: () => void
  onDiscussWithPatient: () => void
  onNotifyTeam: () => void
  // Terminal action — closes the order out (in-range / out-of-range derived
  // from the latest attachment's range). Also closes the drawer.
  onMarkAsRead: () => void
  // Designate / change source + range for the currently-viewed attachment.
  onUpdateAttachment?: (attachmentId: string, patch: Partial<OrderAttachment>) => void
  // Re-tag a viewed history result: Source→deliveryMethod, Range→status.
  onUpdateEntry?: (id: string, updates: Partial<LabEntry>) => void
  // Opens the result-actions modal (edit name / date / delete) from the toolbar
  // dot menu, acting on the subject entry (history result, or the active order).
  onOpenResultMenu?: (entry: LabEntry, type: LabResultModalType) => void
  // Appends uploaded files to the viewed history result ("Add result").
  onAddToResult?: (entryId: string, form: LabUploadForm) => void
  // Re-tag one file of a history result that carries a real file list.
  onUpdateResultAttachment?: (entryId: string, attachmentId: string, patch: Partial<OrderAttachment>) => void
  // 'upload' when opened from a tile's "Add result"; otherwise derived.
  initialMode?: 'view' | 'upload'
}) {
  const isResultView = !!result

  // Unified attachment list: the result's files for a result view, else the
  // active order's real attachments.
  const attachments: OrderAttachment[] = result
    ? resultAttachments(result)
    : (activeLabOrder?.attachments ?? [])

  // Subject entry for the header title + dot menu. Active order → id
  // 'active-order' (the update/delete handlers special-case that id).
  const subjectEntry: LabEntry = result ?? {
    id: 'active-order',
    date: activeLabOrder?.orderedDate ?? '',
    status: activeLabOrder?.status ?? 'submitted',
    tests: activeLabOrder?.tests ?? [],
    vendor: activeLabOrder?.vendor ?? '',
    deliveryMethod: 'in-flight',
  }
  const headerEyebrow = result
    ? `${result.vendor} · ${result.date}`
    : `${activeLabOrder?.patientName ?? ''} · ${activeLabOrder?.vendor ?? ''} · ${activeLabOrder?.orderedDate ?? ''}`

  const [index, setIndex] = useState(0)

  // 'view' shows the PDF slideshow; 'upload' shows the upload form within the
  // same drawer — for a result view that's "Add result".
  const [previewMode, setPreviewMode] = useState<'view' | 'upload'>(
    initialMode ?? (isResultView || attachments.length > 0 ? 'view' : 'upload')
  )
  // Files dropped onto the drawer in view mode; seeds the upload form.
  const [droppedFiles, setDroppedFiles] = useState<File[]>([])
  const [dragActive, setDragActive] = useState(false)
  // dragenter/dragleave fire for every element crossed; the overlay is up
  // while more have been entered than left.
  const dragDepth = useRef(0)

  // Clamp index if the array shrinks underneath us
  const safeIndex = Math.min(index, Math.max(0, attachments.length - 1))
  if (safeIndex !== index) setIndex(safeIndex)
  const current = attachments[safeIndex]

  const prev = () => setIndex(i => (i - 1 + attachments.length) % attachments.length)
  const next = () => setIndex(i => (i + 1) % attachments.length)

  // Submitting the upload form returns the drawer to view mode (so the user
  // sees the freshly attached PDF). Cancelling depends on whether anything is
  // already attached: with attachments, return to view; without, close drawer.
  const handleSubmitFromDrawer = (form: LabUploadForm) => {
    if (result) onAddToResult?.(result.id, form)
    else onInlineUpload(form)
    // Land on the first newly added file (appended after the existing ones).
    if (form.uploads.length > 0) setIndex(attachments.length)
    setDroppedFiles([])
    setPreviewMode('view')
  }
  const handleCancelFromDrawer = () => {
    setDroppedFiles([])
    if (attachments.length === 0) onClose()
    else setPreviewMode('view')
  }

  // Drag-and-drop a result file anywhere on the drawer while viewing. The
  // listeners sit on the document, not the drawer, so the overlay is already
  // up by the time the pointer reaches the PDF iframe — an iframe swallows
  // drag events, so a drawer-level handler would never see a drop over it.
  // In upload mode the form's own drop zone takes over; the document drop
  // handler still stops a missed drop from navigating the tab to the file.
  useEffect(() => {
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files')
    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return
      e.preventDefault()
      dragDepth.current += 1
      if (previewMode === 'view') setDragActive(true)
    }
    const onOver = (e: DragEvent) => { if (hasFiles(e)) e.preventDefault() }
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return
      dragDepth.current = Math.max(0, dragDepth.current - 1)
      if (dragDepth.current === 0) setDragActive(false)
    }
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return
      e.preventDefault()
      dragDepth.current = 0
      setDragActive(false)
    }
    document.addEventListener('dragenter', onEnter)
    document.addEventListener('dragover', onOver)
    document.addEventListener('dragleave', onLeave)
    document.addEventListener('drop', onDrop)
    return () => {
      document.removeEventListener('dragenter', onEnter)
      document.removeEventListener('dragover', onOver)
      document.removeEventListener('dragleave', onLeave)
      document.removeEventListener('drop', onDrop)
    }
  }, [previewMode])

  const handleOverlayDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    dragDepth.current = 0
    setDragActive(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length === 0) return
    setDroppedFiles(files)
    setPreviewMode('upload')
  }

  // ESC backs out one step: in upload mode with attachments, return to view;
  // otherwise close the drawer entirely. Arrow keys page through attachments
  // only when in view mode.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (previewMode === 'upload' && attachments.length > 0) setPreviewMode('view')
        else onClose()
        return
      }
      if (previewMode === 'view') {
        // Inlined (rather than calling prev()/next()) so the effect's deps stay
        // honest — setIndex is stable, prev/next are recreated each render.
        if (e.key === 'ArrowLeft' && attachments.length > 1) setIndex(i => (i - 1 + attachments.length) % attachments.length)
        if (e.key === 'ArrowRight' && attachments.length > 1) setIndex(i => (i + 1) % attachments.length)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [attachments.length, onClose, previewMode])

  // Shared circular nav button styling for the bottom row
  // Chakra props rather than a style object, so these ride on `Pressable`
  // like every other interactive element on the tab.
  const navButtonStyle = {
    w: '40px',
    h: '40px',
    borderRadius: 'full',
    bg: 'canvas',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'cloud',
    color: 'seafoam',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 'lg',
    fontWeight: 'semibold',
    boxShadow: '0 2px 8px rgba(4,51,56,0.08)',
    flexShrink: 0,
    transition: 'background 0.15s, border-color 0.15s',
  } as const

  return (
    <>
      {/* Backdrop covers the LEFT portion of the screen — the drawer covers the right */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(4,51,56,0.45)',
          zIndex: 200,
        }}
      />

      {/* Right-side drawer */}
      <chakra.div
        css={{
          position: 'fixed',
          top: 0, right: 0, bottom: 0,
          width: '65vw',
          minWidth: '700px',
          maxWidth: '1200px',
          background: '{colors.canvas}',
          borderLeft: '1px solid {colors.cloud}',
          boxShadow: '-12px 0 48px rgba(4,51,56,0.18)',
          zIndex: 201,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'slideInRight 250ms ease-out',
        }}
      >
        {/* Header — close × + title block + global actions (Upload + Mark as Read).
            Upload button is hidden in upload mode (the body IS the upload form). */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '16px 24px',
          borderBottom: '1px solid var(--cloud)',
          flexShrink: 0,
        }}>
          <Pressable
            onClick={onClose}
            aria-label="Close preview"
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            w="28px"
            h="28px"
            borderRadius="sm"
            color="bluegrey"
          >
            <CloseIcon />
          </Pressable>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--blue-grey)', letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: 'var(--font-body)', margin: '0 0 2px' }}>
              {headerEyebrow}
            </p>
            <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--evergreen)', fontFamily: 'var(--font-body)', margin: 0, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {subjectEntry.tests.join(', ')}
            </h2>
          </div>
          {/* Upload + Mark as Read are active-order-only (a history result is
              already terminal). */}
          {!isResultView && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {previewMode === 'view' && (
                <Button variant="outline" gap={2} onClick={() => setPreviewMode('upload')}>
                  <UploadIcon /> Upload
                </Button>
              )}
              <Button
                variant="solid"
                onClick={onMarkAsRead}
                disabled={attachments.length === 0}
                title={attachments.length === 0 ? 'Attach at least one result to mark this order as reviewed.' : undefined}
              >
                Mark as Reviewed
              </Button>
            </div>
          )}
        </div>

        {/* Attachment Actions — a single Feather-backgrounded section so the file
            metadata and per-attachment controls read as one connected unit.
            Row 1: file name + read-only reviewer. Row 2: Source/Range dropdowns
            + patient/team communication actions. */}
        {previewMode === 'view' && current && (
          <div style={{ background: 'var(--feather)', borderBottom: '1px solid var(--cloud)', flexShrink: 0 }}>
            {/* Row 1 — file name (left) + reviewer (right, read-only, both contexts) */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 24px',
              borderBottom: '1px solid rgba(4,51,56,0.06)',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, color: 'var(--evergreen)', fontFamily: 'var(--font-body)', fontSize: 14 }}>
                <FileIcon />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current.fileName}</span>
              </span>
              <span style={{ fontSize: 12, color: 'var(--blue-grey)', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                Reviewer: <span style={{ color: 'var(--seafoam)', fontWeight: 600 }}>{ORDERING_CLINICIAN}</span>
              </span>
            </div>
            {/* Row 2 — per-attachment Source/Range dropdowns + patient-communication actions */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 24,
              padding: '12px 24px',
              flexWrap: 'wrap',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--blue-grey)', letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: 'var(--font-body)' }}>Source</span>
                <LabSelect
                  width={150}
                  options={SOURCE_OPTIONS}
                  value={current.source}
                  onChange={v => !isResultView
                    ? onUpdateAttachment?.(current.id, { source: v as OrderAttachment['source'] })
                    : subjectEntry.attachments?.length
                      ? onUpdateResultAttachment?.(subjectEntry.id, current.id, { source: v as OrderAttachment['source'] })
                      : onUpdateEntry?.(subjectEntry.id, { deliveryMethod: v === 'patient-copy' ? 'uploaded' : 'auto-received' })}
                />
              </div>
              {/* A terminal result has already been adjudicated, so the drawer
                  reports the card's own pill instead of offering to re-pick it.
                  The dropdown survives only on an active order, where the range
                  is still being decided — "Mark as Reviewed" stays disabled
                  until every attachment has one. The caption reads Status there
                  because the pill also carries the non-range "Uploaded" state. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--blue-grey)', letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: 'var(--font-body)' }}>
                  {isResultView ? 'Status' : 'Range'}
                </span>
                {isResultView ? (
                  <ResultPill entry={subjectEntry} size="md" />
                ) : (
                  <LabSelect
                    width={150}
                    options={RANGE_OPTIONS}
                    value={current.range}
                    onChange={v => onUpdateAttachment?.(current.id, { range: v as OrderAttachment['range'] })}
                  />
                )}
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* All Clear is hidden in the read-only (terminal) context per PRD. */}
                {!isResultView && (
                  <Button variant="outline" gap={2} onClick={onQuickMessage}>
                    <CheckIcon size={11} /> All Clear
                  </Button>
                )}
                <Button variant="outline" gap={2} onClick={onDiscussWithPatient}>
                  <MessageIcon /> Patient
                </Button>
                <Button variant="outline" gap={2} onClick={onNotifyTeam}>
                  <MessageIcon /> Team
                </Button>
                {/* Dot menu — shown whenever results are attached */}
                {attachments.length > 0 && (
                  <ResultActionsMenu
                    entry={subjectEntry}
                    onOpenModal={onOpenResultMenu}
                    onAddResult={() => setPreviewMode('upload')}
                    iconColor="var(--seafoam)"
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Body — PDF fills the entire frame */}
        <div style={{
          flex: 1, minHeight: 0,
          background: 'var(--bg-subtle)',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {previewMode === 'upload' ? (
            // Upload mode — empty state shows the progressive (drop-zone-only)
            // form with the friendly heading; "add more" mode shows the full
            // form immediately since the user explicitly entered upload mode.
            <div style={{
              position: 'absolute', inset: 0,
              overflowY: 'auto',
            }}>
              <div style={{
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px',
                boxSizing: 'border-box',
              }}>
                <div style={{ maxWidth: 640, width: '100%' }}>
                  {attachments.length === 0 && (
                    <p style={{
                      fontSize: 14, fontWeight: 600, color: 'var(--seafoam)',
                      fontFamily: 'var(--font-body)',
                      textAlign: 'center', margin: '0 0 16px',
                    }}>
                      There are no results attached to this lab order yet.
                    </p>
                  )}
                  {isResultView && (
                    <Text fontSize="sm" fontWeight="semibold" color="seafoam" textAlign="center" mt={0} mb={4}>
                      Add results to {subjectEntry.tests.join(', ')}
                    </Text>
                  )}
                  <LabUploadFormBody
                    onSubmit={handleSubmitFromDrawer}
                    onCancel={handleCancelFromDrawer}
                    progressive={attachments.length === 0}
                    initialFiles={droppedFiles}
                  />
                </div>
              </div>
            </div>
          ) : current?.fileUrl ? (
            <iframe
              key={current.id /* force iframe reload when switching attachments */}
              src={current.fileUrl}
              title={current.fileName}
              style={{
                width: '100%', height: '100%',
                border: 'none', display: 'block',
                background: 'white',
              }}
            />
          ) : (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              textAlign: 'center', color: 'var(--blue-grey)', fontFamily: 'var(--font-body)',
              padding: '0 32px',
            }}>
              <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--seafoam)', margin: '0 0 4px' }}>
                {current?.fileName ?? 'Attachment'}
              </p>
              <p style={{ fontSize: 12, margin: 0 }}>
                PDF preview unavailable — the source file isn't reachable in this session.
              </p>
            </div>
          )}
        </div>

        {/* Bottom row — prev arrow · counter · next arrow */}
        {attachments.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 24px',
            borderTop: '1px solid var(--cloud)',
            flexShrink: 0,
            gap: 16,
          }}>
            {/* Left slot — prev arrow (or empty spacer to balance) */}
            {attachments.length > 1 ? (
              <Pressable onClick={prev} aria-label="Previous attachment" {...navButtonStyle}>‹</Pressable>
            ) : (
              <span style={{ width: 40, height: 40 }} />
            )}

            {/* Center — counter. */}
            <span style={{ fontSize: 12, color: 'var(--blue-grey)', fontFamily: 'var(--font-body)' }}>
              {safeIndex + 1} of {attachments.length}
            </span>

            {/* Right slot — next arrow (or empty spacer to balance) */}
            {attachments.length > 1 ? (
              <Pressable onClick={next} aria-label="Next attachment" {...navButtonStyle}>›</Pressable>
            ) : (
              <span style={{ width: 40, height: 40 }} />
            )}
          </div>
        )}

        {/* Drop overlay — covers the whole drawer (including the iframe) while
            a file drag is in progress in view mode. Dropping stages the files
            in the upload form so Source/Range can be tagged before attaching. */}
        {/* Card with overrides (CLAUDE.md last resort): the package has no
            drop-target surface; Card is the nearest bordered container. */}
        {dragActive && previewMode === 'view' && (
          <VStack
            onDragOver={e => e.preventDefault()}
            onDrop={handleOverlayDrop}
            position="absolute"
            inset={0}
            zIndex={10}
            bg="var(--bg-subtle)"
            p={4}
            alignItems="stretch"
          >
            <Card
              flex={1}
              borderWidth="1.5px"
              borderStyle="dashed"
              borderColor="aqua"
              borderRadius="md"
              bg="transparent"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap="6px"
              pointerEvents="none"
            >
              <Text as="span" color="aqua" display="inline-flex" my={0}>
                <UploadIcon />
              </Text>
              <Text fontSize="sm" fontWeight="semibold" color="seafoam" my={0}>
                Drop to add results to {subjectEntry.tests.join(', ')}
              </Text>
              <Text fontSize="xs" color="bluegrey" my={0}>
                PDFs or JPGs
              </Text>
            </Card>
          </VStack>
        )}
      </chakra.div>
    </>
  )
}

// ── Inline expanded row ───────────────────────────────────────────────────────

function LabOrderFormInline({ form, onChange, onSubmit, onClose, testInput, setTestInput }: {
  form: LabOrderForm
  onChange: (p: Partial<LabOrderForm>) => void
  onSubmit: () => void
  onClose: () => void
  testInput: string
  setTestInput: (v: string) => void
}) {
  // Caption label style — used for all field labels (Figma "Caption" variable)
  const captionStyle: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, color: 'var(--blue-grey)',
    letterSpacing: '0.04em', textTransform: 'uppercase',
    fontFamily: 'var(--font-body)',
    margin: '0 0 8px',
  }

  // Segmented control, shared by Billing Type and Lab Vendor.
  //
  // Not Buttons: a row of Buttons reads as three separate actions rather than
  // one exclusive choice, so approximating it with the Button atom would be
  // worse than leaving it alone.
  //
  // Also not a request. Two call sites in this one file is below the threshold
  // for asking the shared library for anything — a third consumer, ideally
  // outside LabsTab, is what would make it a real pattern rather than a local
  // convenience. Staying local until then.
  const segmentStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '8px 32px',
    borderRadius: 100,
    cursor: 'pointer',
    fontSize: 14, fontWeight: 600,
    fontFamily: 'var(--font-body)',
    transition: 'all 0.15s',
    background: active ? 'white' : 'transparent',
    color: active ? 'var(--seafoam)' : 'var(--blue-grey)',
    boxShadow: active ? '0 1px 3px rgba(4,51,56,0.12)' : 'none',
    border: 'none',
    whiteSpace: 'nowrap',
  })

  return (
    <chakra.div
      css={{
        background: '{colors.canvas}',
        border: '1px solid {colors.cloud}',
        borderRadius: '{radii.card}',
        padding: '24px',
        marginBottom: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      }}
    >
      {/* Header — minimal "New Lab Order", no divider (gap: 24 handles separation) */}
      <h2 style={{
        fontSize: 15, fontWeight: 600, color: 'var(--evergreen)',
        fontFamily: 'var(--font-body)',
        margin: 0, lineHeight: 1.25,
      }}>
        New Lab Order
      </h2>

      {/* Row 1: Billing Type + Lab Vendor */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Billing Type */}
        <div>
          <p style={captionStyle}>Billing Type</p>
          <div style={{ display: 'flex', background: 'var(--feather)', borderRadius: 100, padding: 4, width: '100%' }}>
            {(['insurance', 'self-pay'] as const).map(v => (
              <button
                key={v}
                onClick={() => onChange({ billingType: v })}
                style={segmentStyle(form.billingType === v)}
              >
                {v === 'insurance' ? 'Insurance' : 'Self-pay'}
              </button>
            ))}
          </div>
        </div>

        {/* Lab Vendor */}
        <div>
          <p style={captionStyle}>Lab Vendor</p>
          <div style={{ display: 'flex', background: 'var(--feather)', borderRadius: 100, padding: 4, width: '100%' }}>
            {(['quest', 'labcorp'] as const).map(v => (
              <button
                key={v}
                onClick={() => onChange({ vendor: v })}
                style={segmentStyle(form.vendor === v)}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Row 2: Tests composer (full width) */}
      <TestComposer form={form} onChange={onChange} testInput={testInput} setTestInput={setTestInput} />

      {/* Row 3: Reviewing Provider + Order Date */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Reviewing Provider — locked, Blue Grey text */}
        <div>
          <p style={captionStyle}>Reviewing Provider</p>
          <div style={{
            background: 'var(--bg-subtle)', border: '1px solid var(--cloud)', borderRadius: 8,
            padding: 4,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: 8,
            }}>
              <span style={{
                fontSize: 14, fontWeight: 600, color: 'var(--blue-grey)',
                fontFamily: 'var(--font-body)',
              }}>
                {form.reviewingProvider}
              </span>
              <LockIcon />
            </div>
          </div>
        </div>

        {/* Order Date — locked, Blue Grey text */}
        <div>
          <p style={captionStyle}>Order Date</p>
          <div style={{
            background: 'var(--bg-subtle)', border: '1px solid var(--cloud)', borderRadius: 8,
            padding: 4,
          }}>
            <div style={{ padding: 8 }}>
              <span style={{
                fontSize: 14, fontWeight: 600, color: 'var(--blue-grey)',
                fontFamily: 'var(--font-body)',
              }}>
                {form.orderDate}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer — no divider (gap: 24 handles separation), Place Order disabled when no tests */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
        gap: 24,
      }}>
        <Button variant="link" onClick={onClose}>Cancel</Button>
        <Button variant="solid" onClick={onSubmit} disabled={form.tests.length === 0}>
          Place Order
        </Button>
      </div>
    </chakra.div>
  )
}

// ── Root LabsTab ──────────────────────────────────────────────────────────────

export default function LabsTab({ patientId: _patientId, journeyStage, setJourneyStage, activeLabOrder, setActiveLabOrder, onLabOrderComplete }: {
  patientId: string
  journeyStage: JourneyStage
  setJourneyStage: (s: JourneyStage) => void
  activeLabOrder: ActiveLabOrder | null
  setActiveLabOrder: (order: ActiveLabOrder | null) => void
  onLabOrderComplete?: () => void
}) {
  const [form, setForm] = useState<LabOrderForm>({ ...DEFAULT_FORM })
  const [testInput, setTestInput] = useState('')
  // Whether the inline new-order form is open. Local UI state, not a
  // `journeyStage`: the form is what the header's "+ New Lab Order" button
  // opens, and it's mutually exclusive with the upload card, not a scenario a
  // presenter stages. It previously rode on journeyStage === 'form' | 'review',
  // which made the prototype bar's Stage dropdown claim "Review" while showing
  // the order form.
  const [orderFormOpen, setOrderFormOpen] = useState(false)
  // Highlight-on-new-upload was never wired (setter unused); kept the value so
  // the LabOrderListCard / history-list highlight prop stays connected.
  const [highlightId] = useState<string | undefined>()
  const [toastVisible, setToastVisible] = useState(false)
  const [toastTests, setToastTests] = useState<string[]>([])
  const [toastTitle, setToastTitle] = useState('Lab order placed successfully')
  const [showUpload, setShowUpload] = useState(false)
  const [uploadedEntries, setUploadedEntries] = useState<LabEntry[]>([])
  // Prior lab results from the mock layer. Read-through with a local override:
  // render fetched history until the user edits/removes a row, then the local
  // copy wins (avoids re-seed clobber on refetch).
  const { history } = useLabHistory(_patientId)
  const [historyOverride, setHistoryOverride] = useState<LabEntry[] | null>(null)
  const editableHistory = historyOverride ?? history
  // When true, the current upload flow is resolving an in-flight active order
  // (patient brought results from a different facility). On submit we also
  // clear `activeLabOrder` and use a "results received" toast.
  const [uploadResolvesActiveOrder, setUploadResolvesActiveOrder] = useState(false)

  const updateForm = (partial: Partial<LabOrderForm>) => setForm(f => ({ ...f, ...partial }))

  const handleSubmit = () => {
    const vendorLabel = form.vendor === 'quest' ? 'Quest' : 'Labcorp'
    setActiveLabOrder({
      patientId: 'p1',
      patientName: 'Nina Kelso',
      tests: [...form.tests],
      vendor: vendorLabel,
      orderedDate: form.orderDate,
      status: 'submitted',
    })
    setToastTests([...form.tests])
    setToastTitle('Lab order placed successfully')
    setToastVisible(true)
    setTimeout(() => setToastVisible(false), 5000)
    setOrderFormOpen(false)
    // Placing the order satisfies the "Lab Order Needed" task the alert stands
    // for, so the banner shouldn't outlive it.
    if (journeyStage === 'alert') setJourneyStage('profile')
    onLabOrderComplete?.()
  }

  const handleClose = () => setOrderFormOpen(false)

  // Shared helper — appends one or more attachments to the active order and
  // fires the "Results attached" toast. Reused by both the inline-on-card
  // upload form and the in-drawer empty-state upload form.
  const toAttachments = (uploadForm: LabUploadForm): OrderAttachment[] => {
    const stamp = Date.now()
    return uploadForm.uploads.map((u, i) => ({
      id: `att-${stamp}-${i}`,
      fileName: u.fileName || 'Lab Document',
      source: u.source,
      range: u.resultRange,
      uploadedAt: 'Apr 22, 2026',
      uploadedTimestamp: stamp,
      // Capture a blob URL so the preview drawer can render the actual PDF.
      // Valid for the lifetime of the document.
      fileUrl: URL.createObjectURL(u.file),
    }))
  }

  const appendAttachmentToActiveOrder = (uploadForm: LabUploadForm) => {
    if (!activeLabOrder || uploadForm.uploads.length === 0) return
    const newAttachments = toAttachments(uploadForm)
    const nextAttachments = [...(activeLabOrder.attachments ?? []), ...newAttachments]
    const isTerminal = activeLabOrder.status === 'completed' || activeLabOrder.status === 'out-of-range'
    setActiveLabOrder({
      ...activeLabOrder,
      attachments: nextAttachments,
      // Already reviewed → it's a result tile now, so its range follows its
      // files. In-flight orders wait for Mark as Reviewed as before.
      ...(isTerminal ? { status: statusFromAttachments(nextAttachments) } : {}),
    })
    const count = newAttachments.length
    setToastTests([...activeLabOrder.tests])
    setToastTitle(count === 1 ? 'Results attached' : `${count} results attached`)
    setToastVisible(true)
    setTimeout(() => setToastVisible(false), 5000)
  }

  const handleUploadSubmit = (uploadForm: LabUploadForm) => {
    if (uploadResolvesActiveOrder && activeLabOrder) {
      // Active-order upload via the inline-on-card form. Status stays wherever
      // it was (typically 'submitted'). The clinician chooses when to
      // transition via "Mark as Read".
      appendAttachmentToActiveOrder(uploadForm)
    } else if (uploadForm.targetEntryId) {
      // Generic upload pointed at an existing card via "Add to".
      handleAddToResult(uploadForm.targetEntryId, uploadForm)
    } else {
      // Generic upload: create N standalone uploaded entries in the Results grid.
      if (uploadForm.uploads.length === 0) {
        handleUploadClose()
        return
      }
      const stamp = Date.now()
      // Completion date: user-selected, else today. Stored in display form.
      const completedDisplay = uploadForm.completedDate
        ? isoToDisplay(uploadForm.completedDate)
        : isoToDisplay(todayISO())
      const newEntries: LabEntry[] = uploadForm.uploads.map((u, i) => ({
        id: `lu-${stamp}-${i}`,
        date: completedDisplay,
        // Only an explicit out-of-range tag flips the status; untagged → completed.
        status: u.resultRange === 'out-of-range' ? 'out-of-range' : 'completed',
        tests: [u.fileName || 'Lab Document'],
        vendor: sourceLabel(u.source),
        deliveryMethod: 'uploaded',
        fileName: u.fileName || undefined,
      }))
      setUploadedEntries(prev => [...newEntries, ...prev])
      const count = newEntries.length
      setToastTests(count === 1 ? newEntries[0].tests : [])
      setToastTitle(count === 1 ? 'Lab results uploaded' : `${count} lab results uploaded`)
      setToastVisible(true)
      setTimeout(() => setToastVisible(false), 5000)
    }

    handleUploadClose()
  }

  const handleUploadClose = () => {
    setShowUpload(false)
    setUploadResolvesActiveOrder(false)
  }

  const handleUploadForActiveOrder = () => {
    if (!activeLabOrder) return
    setUploadResolvesActiveOrder(true)
    setShowUpload(true)
    setOrderFormOpen(false) // close the order form if open — mutually exclusive
  }

  const handleUploadOpenGeneric = () => {
    setUploadResolvesActiveOrder(false)
    setShowUpload(true)
    setOrderFormOpen(false) // close the order form if open — mutually exclusive
  }

  // "Mark as Read" — transitions the active order to a terminal status, moving
  // it from Active Orders into the Results gallery. Out of Range if any
  // attachment is tagged out-of-range; otherwise defaults to In Range.
  const handleMarkAsRead = () => {
    if (!activeLabOrder) return
    const attachments = activeLabOrder.attachments ?? []
    const terminalStatus: LabStatus = attachments.some(a => a.range === 'out-of-range')
      ? 'out-of-range'
      : 'completed'
    setActiveLabOrder({ ...activeLabOrder, status: terminalStatus })
    setToastTests([...activeLabOrder.tests])
    setToastTitle(terminalStatus === 'out-of-range' ? 'Order marked as reviewed — Out of Range' : 'Order marked as reviewed — In Range')
    setToastVisible(true)
    setTimeout(() => setToastVisible(false), 5000)
    setPreviewOpen(false)
  }

  // The preview drawer shows either the active order or a clicked history
  // result (`selectedResult`). Opening the active-order preview clears the
  // selected result; closing clears both.
  const [previewOpen, setPreviewOpen] = useState(false)
  // Held by id and looked up live (below), so the drawer reflects edits,
  // re-tags and added results without mirroring them into a snapshot.
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null)
  const [previewInitialMode, setPreviewInitialMode] = useState<'view' | 'upload' | undefined>()
  const handlePreviewOpen = () => { setSelectedResultId(null); setPreviewInitialMode(undefined); setPreviewOpen(true) }
  const handlePreviewClose = () => { setSelectedResultId(null); setPreviewOpen(false) }
  const handleSelectResult = (entry: LabEntry) => { setSelectedResultId(entry.id); setPreviewInitialMode(undefined); setPreviewOpen(true) }
  // Tile menu → "Add result": open that result's drawer straight into upload.
  const handleAddResult = (entry: LabEntry) => { setSelectedResultId(entry.id); setPreviewInitialMode('upload'); setPreviewOpen(true) }

  // Patient communication actions — prototype stubs that fire a confirmation
  // toast. Real implementation would integrate with the messaging platform.
  const handleQuickMessage = () => {
    if (!activeLabOrder) return
    setToastTests([])
    setToastTitle(`Sent "Your results look good" to ${activeLabOrder.patientName}`)
    setToastVisible(true)
    setTimeout(() => setToastVisible(false), 5000)
  }
  const handleDiscussWithPatient = () => {
    if (!activeLabOrder) return
    setToastTests([])
    setToastTitle(`Opening Intercom thread with ${activeLabOrder.patientName}…`)
    setToastVisible(true)
    setTimeout(() => setToastVisible(false), 5000)
  }
  // Ping the care team about these results (prototype stub).
  const handleNotifyTeam = () => {
    setToastTests([])
    setToastTitle('Pinged the care team about these results')
    setToastVisible(true)
    setTimeout(() => setToastVisible(false), 5000)
  }

  const [resultModal, setResultModal] = useState<LabResultModalState | null>(null)

  // The 'done' stage's whole point is that a review produced a new result, so it
  // contributes a tile to the gallery — 5 where the fixtures give 4. Derived
  // from the stage rather than pushed into `uploadedEntries` so switching away
  // from the stage takes the tile with it, and so a presenter can't stack
  // duplicates by selecting Done twice.
  const stagedResults: LabEntry[] = journeyStage === 'done' ? [reviewedDemoResult] : []

  const allHistoryEntries: LabEntry[] = [...stagedResults, ...uploadedEntries, ...editableHistory]
  const selectedResult: LabEntry | null = !selectedResultId ? null
    : selectedResultId === 'active-order'
      ? (activeLabOrder ? activeOrderAsEntry(activeLabOrder) : null)
      : allHistoryEntries.find(e => e.id === selectedResultId) ?? null

  // `previewOpen` can outlive its subject: the render below is gated on there
  // being something to show, so clearing the active order out from under an open
  // drawer (the prototype bar's Stage/Labs controls, or deleting the order) just
  // unmounts it with the flag still true — and the drawer then reappears on its
  // own the moment a new order exists. Disarm it instead.
  // Same for a viewed result that's been deleted out from under the drawer.
  useEffect(() => {
    if (!previewOpen) return
    if (selectedResultId ? !selectedResult : !activeLabOrder) handlePreviewClose()
  }, [previewOpen, selectedResultId, selectedResult, activeLabOrder])

  const handleUpdateEntry = (id: string, updates: Partial<LabEntry>) => {
    if (id === 'active-order' && activeLabOrder) {
      setActiveLabOrder({
        ...activeLabOrder,
        ...(updates.tests ? { tests: updates.tests } : {}),
        ...(updates.date ? { orderedDate: updates.date } : {}),
      })
      return
    }
    setUploadedEntries(prev => prev.map(e => e.id === id ? { ...e, ...updates } : e))
    setHistoryOverride(prev => (prev ?? history).map(e => e.id === id ? { ...e, ...updates } : e))
  }

  const handleDeleteEntry = (id: string) => {
    if (id === 'active-order') {
      setActiveLabOrder(null)
      return
    }
    setUploadedEntries(prev => prev.filter(e => e.id !== id))
    setHistoryOverride(prev => (prev ?? history).filter(e => e.id !== id))
  }

  // Designate / modify source + range for a single attachment from the drawer.
  const handleUpdateAttachment = (attachmentId: string, patch: Partial<OrderAttachment>) => {
    if (!activeLabOrder) return
    const nextAttachments = (activeLabOrder.attachments ?? []).map(a =>
      a.id === attachmentId ? { ...a, ...patch } : a
    )
    const isTerminal = activeLabOrder.status === 'completed' || activeLabOrder.status === 'out-of-range'
    setActiveLabOrder({
      ...activeLabOrder,
      attachments: nextAttachments,
      ...(isTerminal ? { status: statusFromAttachments(nextAttachments) } : {}),
    })
  }

  // Rewrites a history result's file list; its status follows the files.
  const setResultAttachments = (id: string, fn: (atts: OrderAttachment[]) => OrderAttachment[]) => {
    const entry = allHistoryEntries.find(e => e.id === id)
    if (!entry) return
    const attachments = fn(resultAttachments(entry))
    handleUpdateEntry(id, { attachments, status: statusFromAttachments(attachments) })
  }

  // "Add result" / drag-and-drop from a result's drawer.
  const handleAddToResult = (id: string, uploadForm: LabUploadForm) => {
    if (uploadForm.uploads.length === 0) return
    if (id === 'active-order') {
      appendAttachmentToActiveOrder(uploadForm)
      return
    }
    const newAttachments = toAttachments(uploadForm)
    setResultAttachments(id, atts => [...atts, ...newAttachments])
    const entry = allHistoryEntries.find(e => e.id === id)
    setToastTests(entry?.tests ?? [])
    setToastTitle(newAttachments.length === 1 ? 'Result added' : `${newAttachments.length} results added`)
    setToastVisible(true)
    setTimeout(() => setToastVisible(false), 5000)
  }

  const handleUpdateResultAttachment = (id: string, attachmentId: string, patch: Partial<OrderAttachment>) => {
    if (id === 'active-order') {
      handleUpdateAttachment(attachmentId, patch)
      return
    }
    setResultAttachments(id, atts => atts.map(a => a.id === attachmentId ? { ...a, ...patch } : a))
  }

  const anyInlineFormOpen = orderFormOpen || showUpload

  // A full-width inline form takes over the tab: the cards below it are noise
  // while it is open, so the list steps aside. The one form that does NOT hide
  // it is an active-order upload — that one renders *inside* the list, on the
  // order's own card, so hiding the list would hide the form with it.
  const inlineFormReplacesList = orderFormOpen || (showUpload && !uploadResolvesActiveOrder)

  // Alert banner (stage: alert)
  const AlertBanner = () => (
    <chakra.div
      css={{ background: '{colors.canvas}', borderRadius: '{radii.card}' }}
      style={{
        border: '1px solid var(--cloud)',
        borderLeft: '3px solid var(--plum)',
        padding: '12px 14px', marginBottom: 16,
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}
    >
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 'var(--text-cos-btn-sb-size)', fontWeight: 'var(--text-cos-btn-sb-weight)', color: 'var(--evergreen)', fontFamily: 'var(--font-body)', margin: '0 0 2px' }}>
          Lab Order Needed
        </p>
        <p style={{ fontSize: 'var(--text-xs-size)', color: 'var(--blue-grey)', fontFamily: 'var(--font-body)', margin: '0 0 8px' }}>
          Complete lab order for Nina Kelso · Due today
        </p>
        <Button variant="solid" size="md" onClick={() => setOrderFormOpen(true)}>
          Place Order
        </Button>
      </div>
    </chakra.div>
  )

  return (
    <div style={{ padding: '20px 20px 40px', flex: 1 }}>
      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%) } to { transform: translateX(0) } }
        @keyframes toastSlideIn { from { opacity: 0; transform: translateX(24px) } to { opacity: 1; transform: translateX(0) } }
        @keyframes modalFadeIn { from { opacity: 0; transform: scale(0.96) translateY(6px) } to { opacity: 1; transform: scale(1) translateY(0) } }
      `}</style>
      {toastVisible && <SuccessToast title={toastTitle} tests={toastTests} onDismiss={() => setToastVisible(false)} />}

      {/* Alert banner */}
      {journeyStage === 'alert' && <AlertBanner />}

      {/* Tab header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 'var(--text-md-b-size)', fontWeight: 'var(--text-md-b-weight)', color: 'var(--evergreen)', fontFamily: 'var(--font-body)', margin: 0 }}>
          Labs & Documents
        </h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Button
            variant="outline"
            size="md"
            onClick={() => !anyInlineFormOpen && handleUploadOpenGeneric()}
            disabled={anyInlineFormOpen}
          >
            ↑ Upload Results
          </Button>
          <Button
            variant="outline"
            size="md"
            onClick={() => !anyInlineFormOpen && setOrderFormOpen(true)}
            disabled={anyInlineFormOpen}
          >
            + New Lab Order
          </Button>
        </div>
      </div>

      {/* Inline order form */}
      {orderFormOpen && (
        <LabOrderFormInline
          form={form}
          onChange={updateForm}
          onSubmit={handleSubmit}
          onClose={handleClose}
          testInput={testInput}
          setTestInput={setTestInput}
        />
      )}

      {/* Generic upload card — only for non-active-order uploads.
          Active-order resolution renders the upload form inline inside
          the LabOrderListCard via LabHistoryList. */}
      {showUpload && !uploadResolvesActiveOrder && (
        <LabUploadFormInline
          onSubmit={handleUploadSubmit}
          onClose={handleUploadClose}
          showDateField
          existingResults={[
            ...(activeLabOrder ? [activeOrderAsEntry(activeLabOrder)] : []),
            ...allHistoryEntries,
          ]}
        />
      )}

      {/* Lab history — hidden while a form owns the tab (see
          inlineFormReplacesList) */}
      {!inlineFormReplacesList && (
        <LabHistoryList
          entries={allHistoryEntries}
          highlightId={highlightId}
          activeLabOrder={activeLabOrder}
          onUploadActiveOrder={handleUploadForActiveOrder}
          onMarkAsRead={handleMarkAsRead}
          onPreviewActiveOrder={handlePreviewOpen}
          uploadOpen={showUpload && uploadResolvesActiveOrder}
          onUploadSubmit={handleUploadSubmit}
          onUploadCancel={handleUploadClose}
          onUpdateEntry={handleUpdateEntry}
          onDeleteEntry={handleDeleteEntry}
          onOpenModal={(entry, type) => setResultModal({ entry, type })}
          onAddResult={handleAddResult}
          onSelectResult={handleSelectResult}
        />
      )}

      {/* Preview drawer — slides in from the right with real PDFs for the active order */}
      {previewOpen && (selectedResult || activeLabOrder) && (
        <LabOrderPreviewDrawer
          key={selectedResultId ?? 'active-order-preview'}
          activeLabOrder={activeLabOrder}
          result={selectedResult}
          onClose={handlePreviewClose}
          onInlineUpload={appendAttachmentToActiveOrder}
          onQuickMessage={handleQuickMessage}
          onDiscussWithPatient={handleDiscussWithPatient}
          onNotifyTeam={handleNotifyTeam}
          onMarkAsRead={handleMarkAsRead}
          onUpdateAttachment={handleUpdateAttachment}
          onUpdateEntry={handleUpdateEntry}
          onOpenResultMenu={(entry, type) => setResultModal({ entry, type })}
          onAddToResult={handleAddToResult}
          onUpdateResultAttachment={handleUpdateResultAttachment}
          initialMode={previewInitialMode}
        />
      )}

      {/* Lab result action modal (edit name / edit date / delete) */}
      {resultModal && (
        <LabResultModal
          state={resultModal}
          onClose={() => setResultModal(null)}
          onUpdate={handleUpdateEntry}
          onDelete={handleDeleteEntry}
        />
      )}
    </div>
  )
}
